import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, XCircle, AlertTriangle, Pause } from 'lucide-react';

interface JobAlert {
  id: string;
  type: 'success' | 'error' | 'warning' | 'paused';
  title: string;
  description: string;
  timestamp: Date;
}

export const TranslationJobAlerts = () => {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState<JobAlert[]>([]);
  const [lastCheckedJob, setLastCheckedJob] = useState<string | null>(null);

  useEffect(() => {
    // Subscribe to all translation jobs changes
    const channel = supabase
      .channel('job-alerts')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'translation_jobs'
        },
        (payload: any) => {
          const job = payload.new;
          
          // Only show alerts for status changes
          if (job.id === lastCheckedJob) return;
          setLastCheckedJob(job.id);

          // Job completed successfully
          if (job.status === 'completed' && job.failed_cities === 0) {
            const alert: JobAlert = {
              id: job.id,
              type: 'success',
              title: '✅ Translation Job Completed',
              description: `Successfully translated ${job.successful_cities} cities (${job.processed_pages} pages)`,
              timestamp: new Date()
            };
            
            setAlerts(prev => [alert, ...prev].slice(0, 10));
            
            toast({
              title: alert.title,
              description: alert.description,
              duration: 5000,
            });
          }
          
          // Job completed with errors
          else if (job.status === 'completed' && job.failed_cities > 0) {
            const alert: JobAlert = {
              id: job.id,
              type: 'warning',
              title: '⚠️ Job Completed with Errors',
              description: `${job.successful_cities} succeeded, ${job.failed_cities} failed`,
              timestamp: new Date()
            };
            
            setAlerts(prev => [alert, ...prev].slice(0, 10));
            
            toast({
              title: alert.title,
              description: alert.description,
              variant: 'destructive',
              duration: 7000,
            });
          }
          
          // Job failed
          else if (job.status === 'failed') {
            const alert: JobAlert = {
              id: job.id,
              type: 'error',
              title: '❌ Translation Job Failed',
              description: job.error_message || 'Unknown error occurred',
              timestamp: new Date()
            };
            
            setAlerts(prev => [alert, ...prev].slice(0, 10));
            
            toast({
              title: alert.title,
              description: alert.description,
              variant: 'destructive',
              duration: 10000,
            });
          }
          
          // Job paused
          else if (job.status === 'paused') {
            const alert: JobAlert = {
              id: job.id,
              type: 'paused',
              title: '⏸️ Job Paused',
              description: `Job paused at ${job.processed_cities}/${job.total_cities} cities`,
              timestamp: new Date()
            };
            
            setAlerts(prev => [alert, ...prev].slice(0, 10));
          }
        }
      )
      .subscribe();

    // Check for stuck jobs (running for more than 2 hours)
    const checkStuckJobs = async () => {
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
      
      const { data: stuckJobs } = await supabase
        .from('translation_jobs')
        .select('*')
        .eq('status', 'processing')
        .lt('last_updated_at', twoHoursAgo);

      if (stuckJobs && stuckJobs.length > 0) {
        stuckJobs.forEach(job => {
          toast({
            title: '⚠️ Stuck Job Detected',
            description: `Job #${job.id.slice(0, 8)} hasn't updated in 2+ hours. Consider restarting.`,
            variant: 'destructive',
            duration: 15000,
          });
        });
      }
    };

    // Check for stuck jobs every 5 minutes
    const interval = setInterval(checkStuckJobs, 5 * 60 * 1000);
    checkStuckJobs(); // Initial check

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [toast, lastCheckedJob]);

  return null; // This is a background component, no UI
};

export default TranslationJobAlerts;
